// src/pages/syntramfg/SyntraMfgExportImport.jsx
import React, { useState } from "react";
import { Link } from "react-router-dom"; // used only for footer links; no routing inside this file
import { useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faGlobe,
  faIndustry,
  faFileAlt,
  faTruck,
  faDollarSign,
  faUsers,
  faShieldAlt,
  faChartBar,
} from "@fortawesome/free-solid-svg-icons";

/**
 * This file DOES NOT use <BrowserRouter> or <Routes>.
 * It imports components from `./eximcomponents/*` and renders the chosen
 * component inline when a hub card is clicked.
 *
 * Make sure you place the components (CatalogPage.jsx, CertificatesPage.jsx, etc.)
 * under src/pages/syntramfg/eximcomponents/.
 */

// import your components (these are the components YOU will keep in eximcomponents)
import SyntraMfgCatalogPage from "./eximcomponents/SyntraMfgCatalogPage";
import SyntraMfgCertificatesPage from "./eximcomponents/SyntraMfgCertificatesPage";
import SyntraMfgLogisticsPage from "./eximcomponents/SyntraMfgLogisticsPage";
import SyntraMfgPricingPage from "./eximcomponents/SyntraMfgPricingPage";
import SyntraMfgConnectPage from "./eximcomponents/SyntraMfgConnectPage";
import SyntraMfgQualityPage from "./eximcomponents/SyntraMfgQualityPage";
import SyntraMfgTariffsPage from "./eximcomponents/SyntraMfgTariffsPage";
import SyntraMfgDashboardPage from "./eximcomponents/SyntraMfgDashboardPage";

const sections = [
  {
    key: "catalog",
    title: "🌍 Product Catalog (Industrial)",
    desc: "Browse or list industrial products, components, spares, and finished goods with technical specs and compliance flags.",
    icon: faIndustry,
    action: "View Catalog",
  },
  {
    key: "certificates",
    title: "📑 Certifications & Compliance",
    desc: "Upload/verify ISO, CE, BIS and other certificates, declaration of conformity, test reports and export permits.",
    icon: faFileAlt,
    action: "Upload Certificates",
  },
  {
    key: "logistics",
    title: "🚚 Supply Chain & Logistics",
    desc: "Plan multimodal transport (sea/air/road), manage packing lists, freight terms (INCOTERMS) and track live shipments.",
    icon: faTruck,
    action: "Manage Shipments",
  },
  {
    key: "pricing",
    title: "💱 Pricing & Trade Finance",
    desc: "Check commodity & input prices, get indicative freight & insurance, and explore trade finance options (LC, bank guarantees).",
    icon: faDollarSign,
    action: "Check Rates & Finance",
  },
  {
    key: "connect",
    title: "🤝 OEM & Buyer Connect",
    desc: "Post RFQs, connect with OEMs, distributors, and international buyers — share capability statements and lead times.",
    icon: faUsers,
    action: "Find Partners",
  },
  {
    key: "quality",
    title: "🛡️ Risk, Quality & Traceability",
    desc: "Receive alerts for non-conformances, set up traceability (batch/serial), and monitor quality KPIs across shipments and plants.",
    icon: faShieldAlt,
    action: "View Alerts",
  },
  {
    key: "tariffs",
    title: "🌐 Export Controls & Tariffs",
    desc: "Quick checks for HS codes, duty estimators, export restrictions, and destination-specific trade rules.",
    icon: faGlobe,
    action: "Check Tariffs",
  },
  {
    key: "dashboard",
    title: "📊 My Dashboard",
    desc: "Your posted listings, compliance status, active shipments, pending LC requests and operational KPIs at a glance.",
    icon: faChartBar,
    action: "Open Dashboard",
  },
];

// map keys to imported components
const componentMap = {
  catalog: SyntraMfgCatalogPage,
  certificates: SyntraMfgCertificatesPage,
  logistics: SyntraMfgLogisticsPage,
  pricing: SyntraMfgPricingPage,
  connect: SyntraMfgConnectPage,
  quality: SyntraMfgQualityPage,
  tariffs: SyntraMfgTariffsPage,
  dashboard: SyntraMfgDashboardPage,
};

export default function SyntraMfgExportImport() {
  const [activeKey, setActiveKey] = useState(null); // null => show hub grid; otherwise show selected component
  const authState = useSelector((state) => state.authReducer);

  const ActiveComponent = activeKey ? componentMap[activeKey] : null;

  return (
    <div>
      {/* HERO + HUB GRID */}
      <section className="py-12 px-6 md:px-20 bg-gradient-to-r from-green-50 to-green-100 min-h-screen">
        <div className="text-center mb-8">
          <h1 className="text-2xl md:text-3xl font-bold text-green-800 flex items-center justify-center gap-3">
            <FontAwesomeIcon icon={faGlobe} className="w-10 h-10 text-green-700" />
            SyntraMfg Export–Import Hub
          </h1>
          <p className="mt-3 text-gray-700 max-w-2xl mx-auto">
            Connect Indian manufacturers, suppliers and OEMs to global markets — manage compliance, logistics,
            trade finance and buyer discovery from one place.
          </p>

          {authState?.userName && (
            <div className="mt-4 text-sm text-gray-600">
              Welcome, <strong className="text-green-800">{authState.userName}</strong>
            </div>
          )}
        </div>

        {/* If no active component, show the grid; otherwise show the active component area */}
        {!ActiveComponent ? (
          <>
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 max-w-6xl mx-auto">
              {sections.map((s) => (
                <div
                  key={s.key}
                  className="flex flex-col justify-between rounded-2xl shadow-lg hover:shadow-xl transition border border-gray-200 bg-white"
                >
                  <div className="p-6 flex flex-col h-full">
                    <div className="flex items-center gap-3 mb-4">
                      <FontAwesomeIcon icon={s.icon} className="w-7 h-7 text-green-600" />
                      <h3 className="text-lg font-semibold text-gray-800">{s.title}</h3>
                    </div>
                    <p className="text-gray-600 text-sm flex-grow">{s.desc}</p>

                    <div className="mt-4 grid grid-cols-1 gap-2">
                      <button
                        className="w-full bg-green-700 hover:bg-green-800 text-white rounded-xl py-2"
                        onClick={() => setActiveKey(s.key)}
                      >
                        {s.action}
                      </button>

                      {/* optional secondary actions handled in-place (example) */}
                      {s.key === "logistics" && (
                        <button
                          className="w-full border border-green-200 rounded-xl py-2 text-sm text-green-800"
                          onClick={() => {
                            // show logistics component and if that component supports a quote view it can render its internal controls
                            setActiveKey("logistics");
                          }}
                        >
                          Request Freight Quote
                        </button>
                      )}

                      {s.key === "pricing" && (
                        <button
                          className="w-full border border-green-200 rounded-xl py-2 text-sm text-green-800"
                          onClick={() => setActiveKey("pricing")}
                        >
                          Explore Trade Finance
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="max-w-6xl mx-auto mt-8">
              <div className="bg-gray-50 p-4 rounded-lg text-sm text-gray-700">
                Tip: All components are rendered in-place. Click a card to open its component. To return, use the Back button inside the component view.
              </div>
            </div>
          </>
        ) : (
          // ACTIVE COMPONENT VIEW — keep a consistent container + back button
          <div className="max-w-6xl mx-auto mt-6">
            <div className="bg-white rounded-2xl shadow p-4">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-4">
                  <button
                    onClick={() => setActiveKey(null)}
                    className="text-sm text-green-700 hover:underline"
                    aria-label="Back to hub"
                  >
                    ← Back to Hub
                  </button>
                  <h2 className="text-xl font-semibold text-gray-800">
                    {sections.find((s) => s.key === activeKey)?.title}
                  </h2>
                </div>
                <div className="text-sm text-gray-500">
                  {sections.find((s) => s.key === activeKey)?.action}
                </div>
              </div>

              <div className="border-t pt-4">
                {ActiveComponent ? (
                  // render the chosen component
                  <ActiveComponent onClose={() => setActiveKey(null)} />
                ) : (
                  <div className="text-gray-600">Component not found — make sure it is exported from eximcomponents.</div>
                )}
              </div>
            </div>
          </div>
        )}
      </section>

      {/* footer */}
      <footer className="bg-green-700 text-white py-10 px-4 sm:px-6 md:px-20">
        <div className="flex flex-col md:flex-row justify-between items-center text-center md:text-left">
          <p className="text-sm">&copy; 2025 Think5Whys Global DeepTech Solutions. All rights reserved.</p>
          <div className="flex space-x-6 mt-4 md:mt-0 text-sm">
            <Link to="/about-us" className="text-white hover:text-green-400 transition">About</Link>
            <Link to="/privacy-policy" className="text-white hover:text-green-400 transition">Privacy Policy</Link>
            <Link to="/terms-of-use" className="text-white hover:text-green-400 transition">Terms of Use</Link>
            <Link to="/contact-us" className="text-white hover:text-green-400 transition">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
